import { match } from 'ts-pattern'
import { BeaconCoverageDetail } from './beacon-coverage-detail.tsx'
import { CoverageHatch, DEPTH_VARIANTS } from './beacon-coverage-map.tsx'
import * as styles from './beacon-coverage.css.ts'
import { type CoverageEntity, type CoverageModel, depthOf, type EntryPointEntity } from './coverage-model.ts'
import { entryPointKeyOf, selectableProps } from './selectable.ts'

const R0 = 64
const R1 = 128
const R2 = 190
const R3 = 246
const R4 = 300
const RF = 312
const GAP = 0.012

const point = (radius: number, angle: number) =>
  `${(radius * Math.cos(angle)).toFixed(2)},${(radius * Math.sin(angle)).toFixed(2)}`

const arcPath = (r0: number, r1: number, a0: number, a1: number) => {
  const large = a1 - a0 > Math.PI ? 1 : 0
  return `M${point(r0, a0)} L${point(r1, a0)} A${r1},${r1} 0 ${large} 1 ${point(r1, a1)} L${point(r0, a1)} A${r0},${r0} 0 ${large} 0 ${point(r0, a0)}Z`
}

const truncate = (text: string, length: number) =>
  text.length > length ? `${text.slice(0, Math.max(1, length - 1))}…` : text

type Segment =
  | { kind: 'ep'; entity: EntryPointEntity; weight: number }
  | { kind: 'controls'; weight: number }
  | { kind: 'repo'; weight: number }

/**
 * The evidence trail: the repository at the centre, then rings of entry points, operation groups and
 * operations, with the files read for each part on the outside and verified findings on the rim.
 */
export const BeaconCoverageTrail: React.FC<{
  model: CoverageModel
  selectedKey?: string
  onSelect: (key: string) => unknown
}> = ({ model, selectedKey, onSelect }) => {
  const segments: Segment[] = [
    ...model.entryPoints.map(entity => ({
      kind: 'ep' as const,
      entity,
      weight: Math.max(entity.operationKeys.length, 7),
    })),
    { kind: 'controls', weight: model.controls.length * 3 },
    { kind: 'repo', weight: 6 },
  ]
  const total = segments.reduce((sum, segment) => sum + segment.weight, 0)
  const span = 2 * Math.PI - GAP * segments.length
  const maxFiles = Math.max(
    1,
    ...[...model.groups.values()].map(group => group.files),
    ...model.controls.map(control => control.files),
    model.repository.files
  )

  const shapes: React.ReactNode[] = []
  const labels: React.ReactNode[] = []
  const select = (key: string) => ({
    onClick: () => onSelect(key),
    'data-key': key,
  })
  const selectedEntryPoint = entryPointKeyOf(model, selectedKey)
  const selectPart = (entity: CoverageEntity & { entryPointKey: string }) =>
    selectableProps(
      entity.key,
      `${entity.name}: ${entity.status.replace('_', ' ')}`,
      onSelect,
      entity.entryPointKey === selectedEntryPoint
    )
  const fileBar = (a0: number, a1: number, entity: CoverageEntity) =>
    entity.files > 0 && (
      <path
        key={`${entity.key}:files`}
        className={styles.arc({ fill: 'files', isSelected: false })}
        d={arcPath(R3 + 6, R3 + 6 + (R4 - R3 - 6) * (entity.files / maxFiles), a0, a1)}
        {...select(entity.key)}
      />
    )
  const findingDot = (key: string, angle: number) => (
    <circle
      key={key}
      className={styles.findingDot}
      cx={(RF * Math.cos(angle)).toFixed(1)}
      cy={(RF * Math.sin(angle)).toFixed(1)}
      r={3.6}
    />
  )
  const curvedLabel = (id: string, radius: number, a0: number, a1: number, text: string) => {
    const middle = (a0 + a1) / 2
    const isFlipped = Math.sin(middle) > 0.05
    const start = isFlipped ? a1 : a0
    const end = isFlipped ? a0 : a1
    labels.push(
      <g key={id}>
        <path
          id={id}
          d={`M${point(radius, start)} A${radius},${radius} 0 0 ${isFlipped ? 0 : 1} ${point(radius, end)}`}
          fill="none"
        />
        <text className={`${styles.arcLabel} ${styles.halo}`}>
          <textPath href={`#${id}`} startOffset="50%" textAnchor="middle">
            {text}
          </textPath>
        </text>
      </g>
    )
  }

  let angle = -Math.PI / 2
  segments.forEach((segment, index) => {
    const a0 = angle
    const a1 = angle + (segment.weight / total) * span
    angle = a1 + GAP
    const arcLength = (a1 - a0) * ((R0 + R1) / 2)
    if (segment.kind === 'ep') {
      const ep = segment.entity
      const entryFill =
        ep.operationKeys.length === 0
          ? 'd0'
          : match(ep.status)
              .with('examined', () => 'd3' as const)
              .with('partly', () => 'd1' as const)
              .with('not_examined', 'not_applicable', () => 'entry' as const)
              .exhaustive()
      shapes.push(
        <path
          key={ep.key}
          className={styles.arc({ fill: entryFill, isSelected: selectedKey === ep.key })}
          d={arcPath(R0, R1, a0, a1)}
          tabIndex={0}
          aria-label={ep.name}
          onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && onSelect(ep.key)}
          {...select(ep.key)}
        />
      )
      if (arcLength > 50) {
        curvedLabel(`beacon-trail-${index}`, (R0 + R1) / 2 - 4, a0, a1, truncate(ep.name, Math.floor(arcLength / 6.4)))
      }
      if (ep.operationKeys.length === 0) {
        shapes.push(
          <path
            key={`${ep.key}:empty`}
            className={styles.arc({ fill: 'd0', isSelected: false })}
            d={arcPath(R1 + 4, R3, a0, a1)}
            pointerEvents="none"
          />
        )
        return
      }
      let groupStart = a0
      for (const key of ep.groupKeys) {
        const group = model.groups.get(key)
        if (!group) {
          continue
        }
        const groupEnd = groupStart + ((a1 - a0) * group.operationKeys.length) / ep.operationKeys.length
        const depths = group.operationKeys.map(opKey => {
          const operation = model.operations.get(opKey)
          return operation ? depthOf(model, operation) : 0
        })
        const groupDepth = Math.round(depths.reduce((sum, depth) => sum + depth, 0) / Math.max(1, depths.length))
        shapes.push(
          <path
            key={group.key}
            className={styles.arc({
              fill: groupDepth ? (DEPTH_VARIANTS[groupDepth] ?? 'd0') : 'd0',
              isSelected: selectedKey === group.key,
            })}
            d={arcPath(R1 + 4, R2, groupStart, groupEnd)}
            {...selectPart(group)}
          />
        )
        const step = (groupEnd - groupStart) / Math.max(1, group.operationKeys.length)
        group.operationKeys.forEach((opKey, opIndex) => {
          const operation = model.operations.get(opKey)
          if (!operation) {
            return
          }
          const start = groupStart + opIndex * step
          const depth = depthOf(model, operation)
          shapes.push(
            <path
              key={operation.key}
              className={styles.arc({
                fill: depth ? (DEPTH_VARIANTS[depth] ?? 'd0') : 'd0',
                isSelected: selectedKey === operation.key,
              })}
              d={arcPath(R2 + 4, R3, start, start + step)}
              style={{ strokeWidth: step * R3 > 3 ? 0.6 : 0.2 }}
              {...selectPart(operation)}
            />
          )
          if (operation.findings.length > 0) {
            shapes.push(findingDot(`${operation.key}:finding`, start + step / 2))
          }
        })
        shapes.push(fileBar(groupStart, groupEnd, group))
        groupStart = groupEnd
      }
      return
    }
    if (segment.kind === 'controls') {
      shapes.push(
        <path key="controls" className={styles.arc({ fill: 'd3', isSelected: false })} d={arcPath(R0, R1, a0, a1)} />
      )
      if (arcLength > 40) {
        curvedLabel(
          'beacon-trail-controls',
          (R0 + R1) / 2 - 4,
          a0,
          a1,
          arcLength > 120 ? 'Security controls' : 'Controls'
        )
      }
      const step = (a1 - a0) / Math.max(1, model.controls.length)
      model.controls.forEach((control, controlIndex) => {
        const start = a0 + controlIndex * step
        shapes.push(
          <path
            key={control.key}
            className={styles.arc({
              fill: control.status === 'examined' ? 'd3' : 'd0',
              isSelected: selectedKey === control.key,
            })}
            d={arcPath(R1 + 4, R3, start, start + step)}
            tabIndex={0}
            aria-label={control.name}
            onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && onSelect(control.key)}
            {...select(control.key)}
          />
        )
        shapes.push(fileBar(start, start + step, control))
        if (control.findings.length > 0) {
          shapes.push(findingDot(`${control.key}:finding`, start + step / 2))
        }
      })
      return
    }
    const repository = model.repository
    shapes.push(
      <path
        key="repo"
        className={styles.arc({
          fill: repository.status === 'examined' ? 'd3' : 'd0',
          isSelected: selectedKey === 'repo',
        })}
        d={arcPath(R0, R3, a0, a1)}
        tabIndex={0}
        aria-label="Repository-wide hygiene"
        onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && onSelect('repo')}
        {...select('repo')}
      />
    )
    shapes.push(fileBar(a0, a1, repository))
    if (repository.findings.length > 0) {
      shapes.push(findingDot('repo:finding', (a0 + a1) / 2))
    }
  })

  return (
    <>
      <TrailKey />
      <div className={styles.trailGrid}>
        <svg
          viewBox="-345 -345 690 690"
          className={styles.trailSvg}
          role="group"
          aria-label="Sunburst: the repository at the centre; entry points, operation groups and operations in rings; file reads outside"
        >
          <CoverageHatch />
          <circle r={RF} className={styles.ringGuide} />
          <text className={styles.ringLabel} x={0} y={-RF - 10} textAnchor="middle">
            VERIFIED FINDINGS
          </text>
          {shapes}
          {labels}
          <circle r={R0 - 6} className={styles.centerDisc} />
          <text className={styles.centerNumber} x={0} y={-4} textAnchor="middle">
            {Math.round(model.summary.coverage.examined_percent)}%
          </text>
          <text className={styles.ringLabel} x={0} y={13} textAnchor="middle">
            EXAMINED
          </text>
        </svg>
        <TrailLegend model={model} selectedKey={selectedKey} onSelect={onSelect} />
      </div>
    </>
  )
}

const TrailLegend: React.FC<{ model: CoverageModel; selectedKey?: string; onSelect: (key: string) => unknown }> = ({
  model,
  selectedKey,
  onSelect,
}) => (
  <aside className={`${styles.detail} ${styles.trailLegend}`} aria-label="Entry points">
    <p className={styles.subHeading}>Entry points · operations · verified findings</p>
    <ol className={styles.trailList}>
      {model.entryPoints.map(ep => (
        <li
          key={ep.key}
          className={styles.trailListItem}
          tabIndex={0}
          data-key={ep.key}
          onClick={() => onSelect(ep.key)}
          onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && onSelect(ep.key)}
        >
          <i
            className={styles.swatch}
            style={{ background: ep.operationKeys.length ? styles.DEPTH_3 : styles.HATCH_FILL }}
          />
          <span title={ep.name}>{ep.name}</span>
          <span className={styles.findingLocation}>
            {ep.operationKeys.length}
            {ep.findings.length > 0 && <b className={styles.assetKept}> · {ep.findings.length}</b>}
          </span>
        </li>
      ))}
    </ol>
    {selectedKey && <BeaconCoverageDetail model={model} entityKey={selectedKey} />}
  </aside>
)

const TrailKey: React.FC = () => (
  <div className={styles.legend}>
    <span>Rings, inside out: entry points → operation groups → operations → file reads</span>
    <span className={styles.legendItem}>
      <i className={styles.swatch} style={{ background: styles.DEPTH_1 }} />
      light
    </span>
    <span className={styles.legendItem}>
      <i className={styles.swatch} style={{ background: styles.DEPTH_4 }} />
      deep examination
    </span>
    <span className={styles.legendItem}>
      <i className={styles.swatch} style={{ background: styles.HATCH_FILL }} />
      Not examined
    </span>
  </div>
)
